import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";

import { RootState } from "../redux/store";
import {
  fetchEligibleClubsByPhone,
  joinSelectedClubs,
  removeJoinedClubs,
} from "../redux/slices/groupSlice";

const JoinClubCard: React.FC = () => {
  const dispatch = useDispatch<any>();
  const navigate = useNavigate();

  const user = JSON.parse(localStorage.getItem("user") || "{}");

  const { eligibleGroups = [], loading, joining } = useSelector(
    (state: RootState) => state.group
  );

  const [selectedClubs, setSelectedClubs] = useState<any[]>([]);

  const phoneNumber =
    user?.phone_number ||
    user?.user_mobile ||
    localStorage.getItem("public_mobile") ||
    "";

  useEffect(() => {
    if (phoneNumber) {
      dispatch(fetchEligibleClubsByPhone(phoneNumber));
    }
  }, [dispatch, phoneNumber]);

  const totalClubs = eligibleGroups.length;

  const isClubSelected = (contactId: number) => {
    return selectedClubs.some((item) => item.contact_id === contactId);
  };

  const toggleClub = (club: any) => {
    setSelectedClubs((prev) => {
      const exists = prev.some((item) => item.contact_id === club.contact_id);

      if (exists) {
        return prev.filter((item) => item.contact_id !== club.contact_id);
      }

      return [
        ...prev,
        {
          client_id: club.client_id,
          client_name: club.client_name,
          contact_id: club.contact_id,
          contact_name: club.contact_name,
          contact_mobile: club.contact_mobile,
        },
      ];
    });
  };

  const handleJoinClubs = async () => {
    if (!user?.user_id) {
      toast.error("Login required to join club");
      navigate("/login");
      return;
    }

    if (selectedClubs.length === 0) {
      toast.error("Please select at least one club");
      return;
    }

    const res = await dispatch(
      joinSelectedClubs({
        user_id: user.user_id,
        clubs: selectedClubs.map((club: any) => ({
          contact_id: club.contact_id,
        })),
      })
    );

    if (
      joinSelectedClubs.fulfilled.match(res) &&
      (res.payload?.success === true || res.payload?.status === true)
    ) {
      const joinedContactIds = selectedClubs.map(
        (club: any) => club.contact_id
      );

      dispatch(removeJoinedClubs(joinedContactIds));
      setSelectedClubs([]);

      toast.success("Clubs joined successfully");
    } else {
      toast.error(res.payload?.message || "Failed to join clubs");
    }
  };

  if (!phoneNumber) return null;

  return (
    <div
      className="card w-100 border-0 mb-3"
      style={{
        borderRadius: "20px",
        background: "#ffffff",
        border: "1px solid #edf2f7",
        boxShadow: "0 8px 24px rgba(15,23,42,0.04)",
        overflow: "hidden",
      }}
    >
      <div
        className="d-flex align-items-center justify-content-between"
        style={{ padding: "18px 18px 12px" }}
      >
        <div>
          <h4
            className="mb-0"
            style={{
              fontSize: "15px",
              fontWeight: 650,
              color: "#243044",
              lineHeight: "1.25",
            }}
          >
            Join Clubs
          </h4>

          <p
            className="mb-0"
            style={{
              fontSize: "12px",
              fontWeight: 600,
              color: "#8a96a8",
              marginTop: "4px",
            }}
          >
            Select clubs linked to your mobile number
          </p>
        </div>

        {!loading && totalClubs > 0 && (
          <span
            style={{
              minWidth: "24px",
              height: "24px",
              padding: "0 8px",
              borderRadius: "999px",
              background: "#eef5ff",
              color: "#0d6efd",
              fontSize: "11px",
              fontWeight: 700,
              display: "inline-flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            {totalClubs}
          </span>
        )}
      </div>

      {loading && (
        <div
          style={{
            padding: "18px",
            display: "flex",
            alignItems: "center",
            gap: "10px",
          }}
        >
          <div
            className="spinner-border spinner-border-sm text-primary"
            role="status"
          />

          <span
            style={{
              fontSize: "12px",
              fontWeight: 600,
              color: "#8a96a8",
            }}
          >
            Checking eligible clubs...
          </span>
        </div>
      )}

      {!loading && totalClubs === 0 && (
        <div style={{ padding: "18px 18px 22px" }}>
          <div
            style={{
              background: "#f8fafc",
              border: "1px dashed #dbe4ef",
              borderRadius: "16px",
              padding: "22px 16px",
              textAlign: "center",
            }}
          >
            <div
              style={{
                width: "48px",
                height: "48px",
                borderRadius: "50%",
                background: "#eef5ff",
                color: "#0d6efd",
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
                fontSize: "20px",
                marginBottom: "10px",
              }}
            >
              <i className="feather-users" />
            </div>

            <h5
              className="mb-1"
              style={{
                fontSize: "14px",
                fontWeight: 650,
                color: "#334155",
              }}
            >
              No clubs to join
            </h5>

            <p
              className="mb-0"
              style={{
                fontSize: "12px",
                fontWeight: 600,
                color: "#8a96a8",
                lineHeight: "1.5",
              }}
            >
              Your mobile number has no pending club linking.
            </p>
          </div>
        </div>
      )}

      {!loading && totalClubs > 0 && (
        <div style={{ padding: "2px 14px 14px" }}>
          {eligibleGroups.map((club: any, index: number) => {
            const selected = isClubSelected(club.contact_id);

            return (
              <div
                key={`${club.client_id}-${club.contact_id}-${index}`}
                onClick={() => toggleClub(club)}
                style={{
                  background: selected ? "#eff6ff" : "#f8fafc",
                  border: selected
                    ? "1px solid #93c5fd"
                    : "1px solid #edf2f7",
                  borderRadius: "16px",
                  padding: "11px",
                  marginBottom: "10px",
                  display: "flex",
                  alignItems: "flex-start",
                  gap: "10px",
                  cursor: "pointer",
                }}
              >
                <span
                  style={{
                    width: "22px",
                    height: "22px",
                    minWidth: "22px",
                    borderRadius: "7px",
                    border: selected
                      ? "1px solid #2563eb"
                      : "1px solid #cbd5e1",
                    background: selected ? "#2563eb" : "#ffffff",
                    color: selected ? "#ffffff" : "transparent",
                    display: "inline-flex",
                    alignItems: "center",
                    justifyContent: "center",
                    fontSize: "12px",
                    fontWeight: 700,
                    marginTop: "2px",
                  }}
                >
                  ✓
                </span>

                <div style={{ minWidth: 0, flex: 1 }}>
                  <h4
                    className="mb-0"
                    style={{
                      fontSize: "13px",
                      fontWeight: 650,
                      color: "#243044",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                      lineHeight: "1.3",
                    }}
                  >
                    {club.client_name || "Club"}
                  </h4>

                  <span
                    style={{
                      display: "block",
                      fontSize: "12px",
                      fontWeight: 600,
                      color: "#8a96a8",
                      marginTop: "4px",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                    }}
                  >
                    {club.contact_name || "-"} • {club.groups?.length || 0}{" "}
                    group{(club.groups?.length || 0) > 1 ? "s" : ""}
                  </span>

                  {club.groups?.length > 0 && (
                    <div style={{ marginTop: "8px" }}>
                      {club.groups.slice(0, 2).map((group: any) => (
                        <div
                          key={group.group_id}
                          style={{
                            fontSize: "11px",
                            fontWeight: 600,
                            color: "#64748b",
                            background: "#ffffff",
                            border: "1px solid #e2e8f0",
                            borderRadius: "999px",
                            padding: "5px 9px",
                            marginBottom: "5px",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                          }}
                        >
                          {group.group_name}
                        </div>
                      ))}

                      {club.groups.length > 2 && (
                        <span
                          style={{
                            fontSize: "11px",
                            fontWeight: 600,
                            color: "#0d6efd",
                          }}
                        >
                          +{club.groups.length - 2} more
                        </span>
                      )}
                    </div>
                  )}
                </div>
              </div>
            );
          })}

          <button
            type="button"
            onClick={handleJoinClubs}
            disabled={joining || selectedClubs.length === 0}
            style={{
              width: "100%",
              height: "40px",
              borderRadius: "999px",
              border: "none",
              background: "#0d6efd",
              color: "#ffffff",
              fontSize: "13px",
              fontWeight: 700,
              opacity: joining || selectedClubs.length === 0 ? 0.65 : 1,
              cursor:
                joining || selectedClubs.length === 0
                  ? "not-allowed"
                  : "pointer",
            }}
          >
            {joining
              ? "Joining..."
              : selectedClubs.length > 0
              ? `Join ${selectedClubs.length} Club${
                  selectedClubs.length > 1 ? "s" : ""
                }`
              : "Select Club"}
          </button>
        </div>
      )}
    </div>
  );
};

export default JoinClubCard;